import { Link } from "react-router-dom";
import type { StaticPage, PageSection } from "@/mocks/admin";

/* ───────────────────────────────────────────────
   TYPES
   ─────────────────────────────────────────────── */
interface ContentBlock {
  type: "heading" | "text" | "list" | "contact" | "separator";
  content: string;
  lines?: string[];
}

/* ───────────────────────────────────────────────
   HEADING DETECTION
   Heuristic: short line without terminal punctuation,
   starts with a number, or contains known heading words.
   ─────────────────────────────────────────────── */
const HEADING_WORDS = new Set([
  "angaben","kontakt","handelsregister","vertretungsberechtigte",
  "haftungsausschluss","urheberrecht","impressum","datenschutz",
  "verantwortliche stelle","erhebung","verarbeitung","cookies",
  "google analytics","google adsense","ihre rechte","datensicherheit",
  "änderungen","redaktion","werbung","technischer support",
  "über uns","unsere mission","unabhängiger journalismus",
  "die schweiz im fokus","unsere themen","redaktionelle leitung",
  "warum","reichweite","werbeformate","ad-platzierungen",
  "cookie-verwaltung","drittanbieter","was sind cookies",
  "wie verwenden wir","notwendige cookies","funktionale cookies",
  "analytische cookies","werbe-cookies",
]);

function isHeadingLine(line: string): boolean {
  const t = line.trim();
  if (!t || t.length > 70) return false;
  if (/^\d+\.\s+/.test(t)) return true;
  if (/^\d+\)\s+/.test(t)) return true;
  const low = t.toLowerCase();
  for (const hw of HEADING_WORDS) if (low.includes(hw)) return true;
  if (t.length < 55 && !/[.!?]$/.test(t) && !t.includes(",")) return true;
  return false;
}

/* ───────────────────────────────────────────────
   SMART CONTENT PARSER
   Splits raw plain-text content into typed blocks.
   ─────────────────────────────────────────────── */
function parseContentBlocks(raw: string): ContentBlock[] {
  const blocks: ContentBlock[] = [];

  const chunks = raw
    .split(/(?:\n\s*\n|^---+\s*\n|\n---+\s*$|\n---+\n)/)
    .map(c => c.trim())
    .filter(Boolean);

  for (const chunk of chunks) {
    const lines = chunk.split("\n").map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) continue;

    if (isHeadingLine(lines[0]) && lines.length > 1) {
      blocks.push({ type: "heading", content: lines[0] });
      const body = lines.slice(1);
      classifyBody(body, blocks);
      continue;
    }

    if (lines.length === 1 && isHeadingLine(lines[0])) {
      blocks.push({ type: "heading", content: lines[0] });
      continue;
    }

    classifyBody(lines, blocks);
  }

  return blocks;
}

function classifyBody(lines: string[], blocks: ContentBlock[]) {
  if (lines.some(l => /^[-•*]\s+/.test(l))) {
    const joined = lines
      .map(l => l.replace(/^[-•*]\s+/, ""))
      .join("|");
    blocks.push({ type: "list", content: joined });
    return;
  }

  if (lines.length === 1 && lines[0].includes("|")) {
    blocks.push({ type: "list", content: lines[0] });
    return;
  }

  const hasLabels = lines.some(l => /^\w[\w\s]*:/.test(l));
  if (hasLabels && lines.length <= 8) {
    blocks.push({ type: "contact", content: lines.join("\n"), lines });
    return;
  }

  blocks.push({ type: "text", content: lines.join("\n") });
}

/* ───────────────────────────────────────────────
   RENDERERS  —  clean, editorial, no cards
   ─────────────────────────────────────────────── */
function HeadingBlock({ text }: { text: string }) {
  return (
    <h2 className="text-[17px] md:text-lg font-semibold text-hj-dark tracking-tight leading-snug mt-8 first:mt-0">
      {text.replace(/^\d+[.)]\s*/, "")}
    </h2>
  );
}

function TextBlock({ content }: { content: string }) {
  return (
    <p className="text-[15px] md:text-base leading-[1.72] text-hj-gray font-body whitespace-pre-line">
      {content.split("\n").map((line, i, arr) => (
        <span key={i}>
          {(() => {
            const t = line.trim();
            if (t.includes("@") && t.includes(".") && !t.includes(" ")) {
              return (
                <a href={`mailto:${t}`} className="text-hj-red hover:underline transition-colors duration-200">
                  {t}
                </a>
              );
            }
            if (/^https?:\/\//.test(t)) {
              return (
                <a href={t} target="_blank" rel="noopener noreferrer nofollow" className="text-hj-red hover:underline transition-colors duration-200">
                  {t}
                </a>
              );
            }
            return t;
          })()}
          {i < arr.length - 1 && <br />}
        </span>
      ))}
    </p>
  );
}

function ListBlock({ content }: { content: string }) {
  return (
    <ul className="space-y-1.5">
      {content.split("|").map((item, i) => {
        const parts = item.split(":");
        return (
          <li key={i} className="flex items-start gap-2.5 text-hj-gray font-body text-[15px] leading-relaxed">
            <span className="w-1.5 h-1.5 rounded-full bg-hj-red mt-[9px] shrink-0" />
            <span>
              {parts.length > 1 ? (
                <>
                  <span className="font-medium text-hj-dark">{parts[0]}</span>
                  <span className="text-hj-gray">:{parts.slice(1).join(":")}</span>
                </>
              ) : (
                <span className="text-hj-dark">{item}</span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function ContactBlock({ lines }: { lines: string[] }) {
  return (
    <div className="grid gap-2.5">
      {lines.map((line, i) => {
        const label = line.split(":")[0];
        const value = line.replace(/^[^:]+\s*/, "").trim();
        const email = value.match(/[^\s@]+@[^\s@]+\.[^\s@]+/);
        return (
          <div key={i} className="flex items-start gap-2.5 text-[15px] leading-snug">
            <span className="font-medium text-hj-dark shrink-0">{label}</span>
            {email ? (
              <a href={`mailto:${email[0]}`} className="text-hj-red hover:underline font-body">{value}</a>
            ) : (
              <span className="text-hj-gray font-body">{value}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ParsedContent({ blocks }: { blocks: ContentBlock[] }) {
  return (
    <div className="space-y-3">
      {blocks.map((block, idx) => {
        if (block.type === "separator") {
          return <hr key={idx} className="border-hj-gray-border/40 my-4" />;
        }
        return (
          <div key={idx}>
            {block.type === "heading" && <HeadingBlock text={block.content} />}
            {block.type === "text" && <TextBlock content={block.content} />}
            {block.type === "list" && <ListBlock content={block.content} />}
            {block.type === "contact" && block.lines && <ContactBlock lines={block.lines} />}
          </div>
        );
      })}
    </div>
  );
}

/* ───────────────────────────────────────────────
   STRUCTURED SECTION RENDERER (DB sections)
   ─────────────────────────────────────────────── */
function groupSections(sections: PageSection[]): PageSection[][] {
  const groups: PageSection[][] = [];
  let current: PageSection[] = [];
  for (const s of sections) {
    if (s.type === "heading") {
      if (current.length) groups.push(current);
      current = [s];
    } else {
      current.push(s);
    }
  }
  if (current.length) groups.push(current);
  return groups;
}

function renderSection(section: PageSection) {
  switch (section.type) {
    case "heading":
      return (
        <h2 className="text-[17px] md:text-lg font-semibold text-hj-dark tracking-tight leading-snug mt-8 first:mt-0">
          {section.content}
        </h2>
      );
    case "text":
      return (
        <p className="text-[15px] md:text-base leading-[1.72] text-hj-gray font-body whitespace-pre-line">
          {section.content.split("\n").map((line, i, arr) => (
            <span key={i}>
              {line.includes("@") && line.includes(".") ? (
                <a href={`mailto:${line.trim()}`} className="text-hj-red hover:underline transition-colors duration-200">{line.trim()}</a>
              ) : line.startsWith("http") ? (
                <a href={line.trim()} target="_blank" rel="noopener noreferrer nofollow" className="text-hj-red hover:underline transition-colors duration-200">{line.trim()}</a>
              ) : (
                line
              )}
              {i < arr.length - 1 && <br />}
            </span>
          ))}
        </p>
      );
    case "quote":
      return (
        <blockquote className="border-l-[3px] border-hj-red pl-5 py-1 bg-hj-gray-light/40 my-3">
          <p className="text-base italic text-hj-dark font-heading leading-relaxed">{section.content}</p>
        </blockquote>
      );
    case "list":
      return (
        <ul className="space-y-1.5">
          {section.content.split("|").map((item, i) => (
            <li key={i} className="flex items-start gap-2.5 text-hj-gray font-body text-[15px] leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full bg-hj-red mt-[9px] shrink-0" />
              <span>
                <span className="font-medium text-hj-dark">{item.split(":")[0]}</span>
                {item.includes(":") && <span className="text-hj-gray">:{item.split(":").slice(1).join(":")}</span>}
              </span>
            </li>
          ))}
        </ul>
      );
    case "contact_form":
      return (
        <p className="text-sm text-hj-gray font-body">
          Nutzen Sie unser{" "}
          <Link to="/kontakt" className="text-hj-red hover:underline transition-colors duration-200">Kontaktformular</Link>.
        </p>
      );
    case "image":
      return (
        <figure>
          <img src={section.content} alt={section.title || "Bild"} loading="lazy" className="w-full rounded-lg" />
          {section.title && <figcaption className="text-xs text-hj-muted mt-2 text-center font-body">{section.title}</figcaption>}
        </figure>
      );
    case "cta":
      return (
        <div className="p-5 md:p-6 bg-hj-dark rounded-lg text-white">
          <h3 className="text-base font-bold font-heading mb-1">{section.title}</h3>
          <p className="text-sm text-white/70 font-body leading-relaxed">{section.content}</p>
        </div>
      );
    default:
      return null;
  }
}

/* ───────────────────────────────────────────────
   MAIN COMPONENT
   ─────────────────────────────────────────────── */
interface Props {
  page: StaticPage;
  children?: React.ReactNode;
  preview?: boolean;
}

export default function StaticPageRenderer({ page, children, preview }: Props) {
  const hasSections = page.sections && page.sections.length > 0;
  const parsedBlocks = !hasSections && page.content ? parseContentBlocks(page.content) : [];
  const grouped = hasSections ? groupSections(page.sections!) : [];
  const showIntro = hasSections && page.content && page.content.trim().length > 0;

  const wrapperClass = preview
    ? "h-full overflow-y-auto bg-white p-6"
    : "min-h-screen bg-white pt-[100px] md:pt-[130px] pb-16 md:pb-24";

  return (
    <div className={wrapperClass}>
      <div className={preview ? "max-w-full" : "max-w-[680px] mx-auto px-5 md:px-8"}>

        {/* ── Breadcrumb ── */}
        <nav className="flex items-center gap-2 text-[13px] text-hj-muted mb-8 md:mb-10 font-body" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-hj-red transition-colors duration-200">Startseite</Link>
          <span className="text-hj-muted/60">/</span>
          <span className="text-hj-dark font-medium">{page.title}</span>
        </nav>

        {/* ── Title ── */}
        <h1 className="text-[26px] md:text-[34px] font-bold text-hj-dark mb-6 md:mb-8 font-heading leading-[1.15] tracking-tight">
          {page.title}
        </h1>

        {/* ── Intro (lead paragraph) ── */}
        {showIntro && (
          <p className="text-lg md:text-[19px] text-hj-dark/80 leading-[1.65] font-body mb-10 md:mb-12 max-w-prose">
            {page.content}
          </p>
        )}

        {/* ── Thin divider after intro ── */}
        {(showIntro || (!hasSections && parsedBlocks.length > 0)) && (
          <div className="w-full h-px bg-hj-gray-border/60 mb-8 md:mb-10" />
        )}

        {/* ── Parsed plain-text content ── */}
        {!hasSections && parsedBlocks.length > 0 && (
          <ParsedContent blocks={parsedBlocks} />
        )}

        {/* ── Structured sections (DB / mocks) ── */}
        {hasSections && (
          <div className="space-y-2">
            {grouped.map((group, idx) => (
              <div key={idx}>
                <div className="space-y-2">
                  {group.map(s => (
                    <div key={s.id}>{renderSection(s)}</div>
                  ))}
                </div>
                {idx < grouped.length - 1 && (
                  <div className="w-full h-px bg-hj-gray-border/50 my-5 md:my-6" />
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Extra children ── */}
        {children}

        {/* ── Footer ── */}
        {!preview && (
          <div className="mt-14 md:mt-20 pt-5 border-t border-hj-gray-border/50 text-center">
            <p className="text-[12px] text-hj-muted/70 font-body tracking-wide">
              Zuletzt aktualisiert:{" "}
              {new Date(page.lastEdited).toLocaleDateString("de-CH", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Exported parser for admin preview
export { parseContentBlocks };